'use client';

import { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface Signal {
  id: string;
  source: string;
  type: string;
  value: number;
  confidence: number;
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  timestamp: string;
  game: string;
}

interface Decision {
  id: string;
  game: string;
  signal: string;
  score: number;
  confidence: number;
  action: string;
  timing: string;
}

const LIVE_SIGNALS: Signal[] = [
  { id: 's1', source: 'xG Neural', type: 'Model Output', value: 73.2, confidence: 89, priority: 'HIGH', timestamp: '22:41:33', game: 'BOS vs TOR' },
  { id: 's2', source: 'Market Signal', type: 'Line Movement', value: -2.5, confidence: 94, priority: 'CRITICAL', timestamp: '22:41:28', game: 'BOS vs TOR' },
  { id: 's3', source: 'Fatigue Engine', type: 'Proprietary', value: 68, confidence: 82, priority: 'HIGH', timestamp: '22:41:15', game: 'COL vs VGK' },
  { id: 's4', source: 'Momentum ML', type: 'Model Output', value: 61.4, confidence: 76, priority: 'MEDIUM', timestamp: '22:40:58', game: 'TBL vs FLA' },
  { id: 's5', source: 'Goalie Pressure', type: 'Proprietary', value: 88, confidence: 91, priority: 'CRITICAL', timestamp: '22:40:45', game: 'BOS vs TOR' },
  { id: 's6', source: 'Elo Advanced', type: 'Model Output', value: 71.8, confidence: 85, priority: 'HIGH', timestamp: '22:40:30', game: 'COL vs VGK' },
];

const DECISIONS: Decision[] = [
  { id: 'd1', game: 'BOS vs TOR', signal: 'Multi-factor convergence', score: 91.4, confidence: 94, action: 'STRONG BOS', timing: '22:41' },
  { id: 'd2', game: 'COL vs VGK', signal: 'xG + Momentum alignment', score: 84.2, confidence: 88, action: 'COL EDGE', timing: '22:38' },
  { id: 'd3', game: 'TBL vs FLA', signal: 'Market divergence detected', score: 71.8, confidence: 79, action: 'MONITOR', timing: '22:35' },
];

const SIGNAL_STREAM = Array.from({ length: 30 }, (_, i) => ({
  t: i,
  composite: 65 + Math.sin(i * 0.4) * 15 + Math.random() * 8,
  confidence: 75 + Math.cos(i * 0.3) * 10 + Math.random() * 5,
  threshold: 80,
}));

const FACTOR_SCORES = [
  { factor: 'Model Consensus', score: 87, weight: 30 },
  { factor: 'Market Signal', score: 94, weight: 25 },
  { factor: 'Proprietary Data', score: 82, weight: 20 },
  { factor: 'Momentum', score: 71, weight: 15 },
  { factor: 'Context', score: 78, weight: 10 },
];

export default function DecisionEngineClient() {
  const [compositeScore, setCompositeScore] = useState(91.4);
  const [signalCount, setSignalCount] = useState(847);
  const [selectedGame, setSelectedGame] = useState('BOS vs TOR');

  useEffect(() => {
    const interval = setInterval(() => {
      setCompositeScore(prev => +(prev + (Math.random() - 0.5) * 1.5).toFixed(1));
      setSignalCount(prev => prev + Math.floor(Math.random() * 5));
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const priorityColors: Record<string, string> = {
    CRITICAL: 'text-red-400 bg-red-400/10 border-red-400/30',
    HIGH: 'text-orange-400 bg-orange-400/10 border-orange-400/30',
    MEDIUM: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
    LOW: 'text-muted-foreground bg-border/30 border-border',
  };

  const weightedScore = FACTOR_SCORES.reduce((acc, f) => acc + (f.score * f.weight / 100), 0);

  return (
    <div className="min-h-screen bg-background text-foreground p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="BoltIcon" size={20} className="text-red-400" />
            <h1 className="text-xl font-bold text-foreground">Real-Time Decision Engine</h1>
            <span className="px-2 py-0.5 text-2xs font-mono bg-red-400/10 text-red-400 border border-red-400/20 rounded">PHASE QUANT · ÉTAPE 6</span>
            <span className="flex items-center gap-1 px-2 py-0.5 text-2xs font-mono bg-green-400/10 text-green-400 border border-green-400/20 rounded">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />LIVE
            </span>
          </div>
          <p className="text-sm text-muted-foreground">Agrégation signaux temps réel — scoring multi-facteurs haute confiance</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-2xs text-muted-foreground font-mono">SIGNAUX TRAITÉS</div>
            <div className="text-lg font-bold text-primary font-mono">{signalCount.toLocaleString()}</div>
          </div>
          <div className="text-right">
            <div className="text-2xs text-muted-foreground font-mono">COMPOSITE SCORE</div>
            <div className="text-2xl font-bold text-red-400 font-mono">{compositeScore}</div>
          </div>
        </div>
      </div>

      {/* Game Selector */}
      <div className="flex gap-2">
        {DECISIONS.map(d => (
          <button key={d.game} onClick={() => setSelectedGame(d.game)}
            className={`px-4 py-2 rounded-lg border text-xs font-medium transition-all ${selectedGame === d.game ? 'bg-red-400/20 border-red-400 text-red-400' : 'border-border text-muted-foreground hover:border-red-400/50'}`}>
            {d.game}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Live Signal Stream */}
        <div className="col-span-2 bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground">Flux Signaux Temps Réel</h3>
            <span className="text-2xs text-muted-foreground font-mono">{selectedGame}</span>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={SIGNAL_STREAM}>
              <defs>
                <linearGradient id="lgComp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="t" tick={{ fill: '#64748b', fontSize: 9 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 9 }} domain={[50, 100]} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 11 }} />
              <Area type="monotone" dataKey="composite" stroke="#ef4444" fill="url(#lgComp)" strokeWidth={2} name="Score Composite" />
              <Line type="monotone" dataKey="confidence" stroke="#3b82f6" strokeWidth={1.5} dot={false} name="Confiance %" />
              <Line type="monotone" dataKey="threshold" stroke="#475569" strokeWidth={1} dot={false} strokeDasharray="4 2" name="Seuil" />
            </AreaChart>
          </ResponsiveContainer>

          {/* Signal Table */}
          <div className="mt-3 space-y-1.5 max-h-48 overflow-y-auto">
            {LIVE_SIGNALS.filter(s => s.game === selectedGame || selectedGame === 'ALL').map(signal => (
              <div key={signal.id} className="flex items-center gap-3 p-2 bg-background rounded border border-border text-xs">
                <span className="text-muted-foreground font-mono w-16 flex-shrink-0">{signal.timestamp}</span>
                <span className={`px-1.5 py-0.5 rounded border text-2xs font-mono flex-shrink-0 ${priorityColors[signal.priority]}`}>{signal.priority}</span>
                <span className="text-foreground flex-1">{signal.source}</span>
                <span className="text-muted-foreground">{signal.type}</span>
                <span className="font-mono font-bold text-foreground w-12 text-right">{signal.value > 0 ? signal.value : `${signal.value}`}</span>
                <span className="font-mono text-green-400 w-10 text-right">{signal.confidence}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Multi-Factor Scoring */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Scoring Multi-Facteurs</h3>
          <div className="space-y-2.5">
            {FACTOR_SCORES.map(f => (
              <div key={f.factor}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{f.factor}</span>
                  <span className="font-mono text-foreground">{f.score} <span className="text-muted-foreground text-2xs">×{f.weight}%</span></span>
                </div>
                <div className="h-2 bg-border rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${f.score >= 85 ? 'bg-green-400' : f.score >= 70 ? 'bg-yellow-400' : 'bg-red-400'}`}
                    style={{ width: `${f.score}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-background rounded border border-red-400/20">
            <div className="text-2xs text-red-400 font-mono mb-1">WEIGHTED COMPOSITE</div>
            <div className="text-3xl font-bold text-red-400 font-mono text-center">{weightedScore.toFixed(1)}</div>
          </div>

          {/* Decisions */}
          <div className="mt-3 space-y-2">
            {DECISIONS.map(d => (
              <div key={d.id} className={`p-2.5 rounded border ${d.score >= 85 ? 'border-green-400/30 bg-green-400/5' : 'border-border bg-background'}`}>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium text-foreground">{d.game}</span>
                  <span className={`text-xs font-bold font-mono ${d.score >= 85 ? 'text-green-400' : 'text-yellow-400'}`}>{d.action}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-2xs text-muted-foreground">Score: <span className="text-foreground font-mono">{d.score}</span></span>
                  <span className="text-2xs text-muted-foreground">Conf: <span className="text-green-400 font-mono">{d.confidence}%</span></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
